import React, { useState } from 'react';
import * as R from 'ramda';
import {
  Box,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  ButtonGroup,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { SaveButton } from 'components/UIElements/UIButton';
import CloseButton from 'components/CustomButtons/CloseButton';
import { getAllowedShortcutActions } from 'reducers/cafaConfigs';
import { saveCafaConfig } from 'actions/integrations/CafaConfigs';
import { CAFA_LEVELS, INTEGRATIONS, INTEGRATION_TYPES } from 'constants/CAFA';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { RootState } from 'reducers';
import {
  getIsConfigurableShortcutAction,
  ShortcutAction,
  DEFAULT_SHORTCUT_CONFIGURATION,
} from 'constants/shortcuts';
import { StrictEntries } from 'utils';

type Props = {
  onClose: () => void;
};

function AllowedKeyboardShortcuts({ onClose }: Props) {
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();
  const { t } = useTranslation('settingsShortcuts');
  const allowedShortcuts = useSelector(getAllowedShortcutActions);
  const [shortcuts, setShortcuts] = useState(allowedShortcuts);

  function toggleAllTo(value: boolean) {
    // `escape` and `confirm` should always remain enabled
    setShortcuts(cur =>
      R.pipe(
        R.toPairs,
        R.map(([key, _curVal]) => [
          key,
          getIsConfigurableShortcutAction(key) ? value : true,
        ]),
        R.fromPairs,
      )(cur),
    );
  }
  function toggleKey(key: ShortcutAction) {
    setShortcuts(cur => ({
      ...cur,
      // `escape` and `confirm` should always remain enabled
      [key]: getIsConfigurableShortcutAction(key) ? !cur[key] : true,
    }));
  }

  const warehouseID = useSelector(getSelectedWarehouseID);

  const save = async () => {
    await dispatch(
      saveCafaConfig({
        integrationName:
          INTEGRATIONS.posConfigurations.allowedKeyboardShortcutActions,
        integrationType: INTEGRATION_TYPES.posConfigurations,
        level: { level: CAFA_LEVELS.Warehouse, id: warehouseID },
        config: shortcuts,
      }),
    );
    onClose();
  };

  return (
    <div data-testid="allowed-keyboard-shortcuts">
      <Box
        display="flex"
        alignItems="center"
        padding="1rem"
        borderBottom="1px solid #dee2e6"
      >
        <Box paddingLeft="5px" fontSize="24px" fontWeight={700}>
          {t('title')}
        </Box>
        <Box flexGrow={1} />
        <SaveButton action={save} variant="POS" />
        <CloseButton action={onClose} />
      </Box>
      <Box padding="1rem">
        <Box display="flex" padding="1rem">
          <ButtonGroup variant="contained" disableElevation>
            <Button
              color="primary"
              data-test-key="enable-all"
              onClick={() => toggleAllTo(true)}
            >
              {t('enableAll')}
            </Button>
            <Button
              color="primary"
              data-test-key="disable-all"
              onClick={() => toggleAllTo(false)}
            >
              {t('disableAll')}
            </Button>
          </ButtonGroup>
        </Box>

        <Grid container direction="column">
          {(Object.entries(DEFAULT_SHORTCUT_CONFIGURATION) as StrictEntries<
            typeof DEFAULT_SHORTCUT_CONFIGURATION
          >).map(([key, _defaultValue]) => (
            <Grid item key={key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shortcuts[key]}
                    disabled={!getIsConfigurableShortcutAction(key)}
                    onChange={() => toggleKey(key)}
                  />
                }
                label={t(`shortcuts.${key}`)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </div>
  );
}

export default AllowedKeyboardShortcuts;
